import { http } from '@/services/api';

export interface StudyScheduleDTO {
  id: number;
  contact_id: number;
  day_of_week: number;
  time: string;
  created_at: string;
  updated_at: string;
}

export interface AddressDTO {
  id: number;
  formatted?: string;
  street?: string | null;
  neighborhood_id?: number | null;
  city_id?: number | null;
  uf?: string | null;
  lat?: number | null;
  lng?: number | null;
  region_id?: number | null;
  created_by?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  ceps?: unknown;
}

export interface ContactDTO {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  phone2?: string | null;
  gender?: 'masculino' | 'feminino' | null;
  age_group?: 'crianca' | 'jovem' | 'adulto' | 'senior' | null;
  // Backend may return either a string or a full address object
  address?: string | AddressDTO | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_lat?: number | null;
  address_lng?: number | null;
  consent_whatsapp?: boolean;
  status: 'active' | 'inactive' | 'potential' | 'converted' | 'absent' | 'refused' | 'completed' | 'dropped' | 'abandoned';
  notes?: string | null;
  team_id: number | null;
  region_id: number | null;
  created_at: string;
  updated_at: string;
  team?: { id: number; name: string } | null;
  region?: { id: number; name: string } | null;
  study_schedules?: StudyScheduleDTO[];
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number | string;
  total: number;
}

export interface NearContactDTO {
  id: number;
  name: string;
  status: 'active' | 'inactive' | 'potential' | 'converted' | 'absent' | 'refused' | 'completed' | 'dropped' | 'abandoned';
  address: string | null;
  team_id: number | null;
  region_id: number | null;
  lat: number;
  lng: number;
  street: string | null;
  distance_m: number;
}

export interface ContactFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'potential' | 'converted' | 'absent' | 'refused' | 'completed' | 'dropped' | 'abandoned';
  statuses?: Array<'active' | 'inactive' | 'potential' | 'converted' | 'absent' | 'refused' | 'completed' | 'dropped' | 'abandoned'>;
  region_id?: number;
  team_id?: number;
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'email' | 'phone' | 'status' | 'created_at' | 'updated_at';
  sort_dir?: 'asc' | 'desc';
}

export const contactsService = {
  async list(filters: ContactFilters = {}): Promise<Paginated<ContactDTO>> {
    const params = new URLSearchParams();
    // Cache buster to avoid serving stale list right after create/update/delete
    params.set('_ts', String(Date.now()));
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.statuses && filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','));
    if (typeof filters.region_id === 'number') params.set('region_id', String(filters.region_id));
    if (typeof filters.team_id === 'number') params.set('team_id', String(filters.team_id));
    if (typeof filters.page === 'number') params.set('page', String(filters.page));
    if (typeof filters.per_page === 'number') params.set('per_page', String(filters.per_page));
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
    return http.get<Paginated<ContactDTO>>(`/contacts${params.toString() ? `?${params.toString()}` : ''}`);
  },

  async show(id: number): Promise<ContactDTO> {
    return http.get<ContactDTO>(`/contacts/${id}`);
  },

  async create(contact: Omit<ContactDTO, 'id' | 'created_at' | 'updated_at'>): Promise<ContactDTO> {
    return http.post<ContactDTO>('/contacts', contact);
  },

  async update(id: number, contact: Partial<ContactDTO>): Promise<ContactDTO> {
    return http.put<ContactDTO>(`/contacts/${id}`, contact);
  },

  async delete(id: number): Promise<void> {
    await http.delete(`/contacts/${id}`);
  },

  async getNearby(params: { lat: number; lng: number; radius?: number }): Promise<NearContactDTO[]> {
    const qs = new URLSearchParams({ lat: String(params.lat), lng: String(params.lng) });
    if (typeof params.radius === 'number') qs.set('radius', String(params.radius));
    return http.get<NearContactDTO[]>(`/contacts/nearby?${qs.toString()}`);
  },

  async getStudySchedules(contactId: number): Promise<StudyScheduleDTO[]> {
    return http.get<StudyScheduleDTO[]>(`/contacts/${contactId}/study-schedules`);
  },

  async addStudySchedule(contactId: number, schedule: Omit<StudyScheduleDTO, 'id' | 'contact_id' | 'created_at' | 'updated_at'>): Promise<StudyScheduleDTO> {
    return http.post<StudyScheduleDTO>(`/contacts/${contactId}/study-schedules`, schedule);
  },

  async updateStudySchedule(contactId: number, scheduleId: number, schedule: Partial<StudyScheduleDTO>): Promise<StudyScheduleDTO> {
    return http.put<StudyScheduleDTO>(`/contacts/${contactId}/study-schedules/${scheduleId}`, schedule);
  },

  async deleteStudySchedule(contactId: number, scheduleId: number): Promise<void> {
    await http.delete(`/contacts/${contactId}/study-schedules/${scheduleId}`);
  }
};
